import { useAuth } from "../../auth/useAuth";
import { useState, type FormEvent } from "react";
import { useBasePage } from "../../hooks/useBasePage";
import type { TenantThemeSettings } from "../../models/TenantThemeSettings";
import { useTenant } from "../../tenant/useTenant";
import { Tenant as TenantObj } from "../../models/Tenant";
import { DefaultTenantTheme } from "../../theme/PantryStackTheme";
import { DataState } from "../../models/DataState";

export function TenantPage() {
  const [IsSaving, SetIsSaving] = useState(false);
  const [SaveMessage, SetSaveMessage] = useState("");
  const { hasPermission, state: authState } = useAuth();
  const { Tenant, TenantUrl, PreviewTheme, SaveTenant } = useTenant();
  const { state, setStateProperties, handleError } = useBasePage<TenantObj>({
    Code: Tenant?.Code,
    DataState: DataState.Unchanged,
    BusinessName: Tenant?.BusinessName ?? "",
    Addresses: Tenant?.Addresses.length ? Tenant.Addresses : [""],
    Theme: Tenant?.Theme ?? DefaultTenantTheme,
    LogoUrl: Tenant?.LogoUrl,
    Email: Tenant?.Email,
    TenantUrl: Tenant?.TenantUrl,
    BusinessPhones: Tenant?.BusinessPhones,
    DefaultWareHouseName: Tenant?.DefaultWareHouseName,
  });

  const SetProperty = <K extends keyof TenantObj>(
    property: K,
    value: TenantObj[K],
  ): void => {
    SetSaveMessage("");
    setStateProperties({
      [property]: value,
      DataState:
        state.DataState === DataState.Unchanged
          ? DataState.Changed
          : state.DataState,
    });
  };

  const SetThemeProperty = <K extends keyof TenantThemeSettings>(
    property: K,
    value: TenantThemeSettings[K],
  ): void => {
    const Theme = { ...state.Theme, [property]: value };
    SetProperty("Theme", Theme);
    PreviewTheme(Theme);
  };

  const SetAddress = (index: number, value: string): void => {
    const Addresses = [...state.Addresses];
    Addresses[index] = value;
    SetProperty("Addresses", Addresses);
  };

  const AddAddress = (): void => {
    SetProperty("Addresses", [...state.Addresses, ""]);
  };

  const RemoveAddress = (index: number): void => {
    if (state.Addresses.length > 1) {
      SetProperty(
        "Addresses",
        state.Addresses.filter((_, itemIndex) => itemIndex !== index),
      );
    }
  };

  const SetLogo = (files: File[]): void => {
    const Logo = files[0];
    SetProperty("LogoFile", Logo);
    SetProperty("LogoUrl", Logo ? URL.createObjectURL(Logo) : state.LogoUrl);
  };

  const Submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (IsSaving) return;
    if (
      !(
        hasPermission("Tenant.Edit") ||
        (authState.RoleUis === undefined && hasPermission("Tenant.Manage"))
      )
    )
      return;

    if (!Tenant) {
      return;
    }

    const Addresses = state.Addresses.map((address) => address.trim()).filter(
      Boolean,
    );

    if (Addresses.length === 0) {
      handleError(new Error("At least one business address is required."));
      return;
    }

    SetIsSaving(true);
    SetSaveMessage("");
    try {
      const updatedTenant = await SaveTenant({
        ...Tenant,
        ...state,
        Code: state.Code,
        BusinessName: state.BusinessName.trim(),
        Addresses,
        TenantUrl,
        Theme: state.Theme,
        LogoFile: state.LogoFile,
        LogoUrl: state.LogoUrl,
        BusinessPhones: state.BusinessPhones,
        DefaultWareHouseName: state.DefaultWareHouseName?.trim() || undefined,
        DataState: DataState.Changed,
      });
      setStateProperties({ ...updatedTenant, DataState: DataState.Unchanged });
      SetSaveMessage("Tenant settings saved successfully.");
    } catch (error) {
      handleError(error);
    } finally {
      SetIsSaving(false);
    }
  };

  return {
    state,
    IsSaving,
    SaveMessage,
    TenantUrl,
    SetProperty,
    SetThemeProperty,
    SetAddress,
    AddAddress,
    RemoveAddress,
    SetLogo,
    Submit,
  };
}
